import React, { useMemo, useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Space, Tag, Typography, Row, Col, Card, Alert, Divider, Tooltip, Radio } from 'antd';
import { SettingOutlined, ReloadOutlined, SearchOutlined, SaveOutlined, AppstoreOutlined, InfoCircleOutlined, CheckCircleOutlined, UserOutlined, StarFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../interfaces/Global';
import { useConfiguracaoPlanos } from './useConfiguracaoPlanos';
import { IConfigPlano } from '../interface';
import HeaderAcoesTabela from '../../../components/HeaderAcoesTabela';
import { TabsConfiguracoes } from '../components/TabsConfiguracoes';
import { useGetColumnSearchPropsTypeString, useGetColumnSearchPropsObj } from '../../../hooks/FilterTable/objString';
import { TextAreaDetalhamento } from '../components/TextAreaDetalhamento';
import { MunicipiosOrdenavelList } from './components/MunicipiosOrdenavelList';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const formatarMoeda = (valor?: number | string | null) => {
    if (valor === undefined || valor === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valor));
};

const normalizarTexto = (texto: any): string => {
    return String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
};

const ConfiguracaoPlanos: React.FC = () => {
    const isDark = useSelector((state: stateGlobal) => state?.global?.theme === 'dark');
    const {
        form,
        configs,
        planosBase,
        loading,
        modalVisible,
        submitting,
        editingConfig,
        contratacaoHoras,
        setContratacaoHoras,
        setModalVisible,
        handleNovo,
        handleEditar,
        handleExcluir,
        handleSalvar,
        carregarDados,

        // Tab 2: Configurações Gerais
        formGeral,
        activeTab,
        setActiveTab,
        loadingGeral,
        salvandoGeral,
        cidadesAtivas,
        loadingCidadesAtivas,
        usuarios,
        loadingUsuarios,
        carregarConfigGeral,
        handleSalvarConfigGeral
    } = useConfiguracaoPlanos();

    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    const [filteredCount, setFilteredCount] = useState<number | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const searchPropsPlanoBase = useGetColumnSearchPropsObj('plano', 'nome');
    const searchPropsDescricao = useGetColumnSearchPropsTypeString('descricao_plano');

    useEffect(() => {
        setFilteredCount(null);
    }, [configs]);

    const possuiTelaForm = Form.useWatch('possui_tela', form);

    const selectedConfig = useMemo(() => {
        return configs.find(c => c.id === selectedRowKeys[0]);
    }, [configs, selectedRowKeys]);

    const columns = [
        {
            title: 'Plano Base',
            dataIndex: 'plano',
            key: 'plano',
            ...searchPropsPlanoBase,
            sorter: (a: IConfigPlano, b: IConfigPlano) => (a.plano?.nome || a.descricao_plano || '').localeCompare(b.plano?.nome || b.descricao_plano || ''),
            showSorterTooltip: false,
            render: (_: any, record: IConfigPlano) => (
                <div>
                    <Space size={6} wrap align="center">
                        <Text strong>{record.plano?.nome || record.descricao_plano || 'Plano'}</Text>
                        {record.is_principal && (
                            <Tag color="gold" style={{ fontSize: '11px', margin: 0, fontWeight: 600 }}>
                                Principal
                            </Tag>
                        )}
                    </Space>
                    <div style={{ fontSize: '12px', color: isDark ? '#52c41a' : '#2e7d32' }}>
                        {formatarMoeda(record.plano?.valor)} / licença
                    </div>
                </div>
            )
        },
        {
            title: 'Descrição',
            dataIndex: 'descricao_plano',
            key: 'descricao_plano',
            ...searchPropsDescricao,
            sorter: (a: IConfigPlano, b: IConfigPlano) => (a.descricao_plano || '').localeCompare(b.descricao_plano || ''),
            showSorterTooltip: false,
        },
        {
            title: 'Contratação Horas',
            dataIndex: 'contratacao_horas',
            key: 'contratacao_horas',
            align: 'center' as const,
            filters: [
                { text: 'Sim', value: true },
                { text: 'Não', value: false }
            ],
            onFilter: (value: any, record: IConfigPlano) => record.contratacao_horas === value,
            render: (ativo: boolean, record: IConfigPlano) => (
                ativo ? (
                    <Tag color="blue">Sim</Tag>
                ) : (
                    <Tag color="default">Não</Tag>
                )
            )
        },
        {
            title: 'Dispositivo / Tela',
            dataIndex: 'possui_tela',
            key: 'possui_tela',
            align: 'center' as const,
            filters: [
                { text: 'Tela 85"', value: 'tela85' },
                { text: 'Tela 75"', value: 'tela' },
                { text: 'Totem 32"', value: 'totem' },
                { text: 'Não possui', value: 'nenhum' }
            ],
            onFilter: (value: any, record: IConfigPlano) => {
                if (value === 'nenhum') return !record.possui_tela;
                if (value === 'totem') return Boolean(record.possui_tela && record.tipo_tela === 'totem');
                if (value === 'tela85') return Boolean(record.possui_tela && record.tipo_tela === 'tela85');
                if (value === 'tela' || value === 'tela75') return Boolean(record.possui_tela && record.tipo_tela !== 'totem' && record.tipo_tela !== 'tela85');
                return true;
            },
            render: (possui: boolean, record: IConfigPlano) => (
                possui ? (
                    record.tipo_tela === 'totem' ? (
                        <Tag color="cyan">Totem 32"</Tag>
                    ) : record.tipo_tela === 'tela85' ? (
                        <Tag color="purple">Tela 85"</Tag>
                    ) : (
                        <Tag color="success">Tela 75"</Tag>
                    )
                ) : (
                    <Tag color="default">Não</Tag>
                )
            )
        }
    ];

    return (
        <div style={{ padding: isMobile ? '12px 8px' : '24px' }}>
            <div style={{ marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>
                    <SettingOutlined /> Configurações de Propostas Comerciais
                </Title>
                <Text type="secondary">
                    Gerencie os modelos de planos, valor fixo por hora e a lista de municípios em destaque no slide de apresentação.
                </Text>
            </div>

            <TabsConfiguracoes activeKey={activeTab} onChange={setActiveTab} />

            {activeTab === 'planos' && (
                <div>
                    <HeaderAcoesTabela
                        showAdd={true}
                        addText="Adicionar"
                        disabledAdd={Boolean(selectedConfig)}
                        onAdd={handleNovo}

                        showEdit={true}
                        editText="Editar"
                        disabledEdit={selectedRowKeys.length !== 1}
                        onEdit={() => {
                            if (selectedConfig) {
                                handleEditar(selectedConfig);
                            }
                        }}

                        showInativar={true}
                        inativarText="Excluir"
                        disabledInativar={selectedRowKeys.length !== 1}
                        onInativar={() => {
                            if (selectedRowKeys.length === 1) {
                                handleExcluir(selectedRowKeys[0]);
                            }
                        }}

                        showCounter={true}
                        filteredCount={filteredCount !== null ? filteredCount : configs.length}
                        totalCount={configs.length}
                    />
                    <Table
                        columns={columns}
                        dataSource={configs}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: 800 }}
                        rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys,
                            onChange: (keys) => setSelectedRowKeys(keys as number[])
                        }}
                        onChange={(pagination, filters, sorter, extra) => {
                            setFilteredCount(extra.currentDataSource.length);
                        }}
                        onRow={(record) => ({
                            onClick: () => {
                                setSelectedRowKeys(prev =>
                                    prev.includes(record.id) ? [] : [record.id]
                                );
                            },
                            style: { cursor: 'pointer' }
                        })}
                        pagination={{
                            pageSize: 10,
                            showTotal: (total) => `Total de ${total} configuração(ões)`
                        }}
                    />
                    <Modal
                        title={editingConfig ? `Editar Configuração #${editingConfig.id}` : 'Nova Configuração de Plano Comercial'}
                        visible={modalVisible}
                        onCancel={() => setModalVisible(false)}
                        onOk={() => form.submit()}
                        confirmLoading={submitting}
                        width={isMobile ? '96vw' : 720}
                        style={{ top: isMobile ? 12 : 24, maxWidth: '100vw' }}
                        bodyStyle={{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', padding: isMobile ? '12px 8px' : '16px 20px' }}
                        okText="Salvar"
                        cancelText="Cancelar"
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSalvar}
                        >
                            {/* Identificação do Plano */}
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="plano_id"
                                        label={<span style={{ fontWeight: 600, fontSize: '13px', color: isDark ? '#ffffffd9' : undefined }}>Plano Base</span>}
                                        rules={[{ required: true, message: 'Selecione o plano base' }]}
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Select placeholder="Selecione um plano cadastrado">
                                            {planosBase.map((p: any) => (
                                                 <Option key={p.id} value={p.id}>
                                                    {p.nome} - {formatarMoeda(p.valor)}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="descricao_plano"
                                        label={<span style={{ fontWeight: 600, fontSize: '13px', color: isDark ? '#ffffffd9' : undefined }}>Descrição do Plano Comercial</span>}
                                        rules={[{ required: true, message: 'Informe uma descrição' }]}
                                        tooltip="Preenche a descrição dentro da tabela de licenças no slide de valores e condições"
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Input placeholder="Ex: Licença Intermediária do aplicativo..." />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Objeto da Proposta */}
                            <Form.Item
                                name="texto_objeto_padrao"
                                label={<span style={{ fontWeight: 600, fontSize: '13px', color: isDark ? '#ffffffd9' : undefined }}>Texto do Objeto da Proposta (Slide 4)</span>}
                                rules={[{ required: true, message: 'Informe o texto padrão do objeto' }]}
                                style={{ marginBottom: 14 }}
                            >
                                <TextArea 
                                    rows={4} 
                                    placeholder="Texto do objeto que constará na proposta comercial..." 
                                    style={{ fontSize: '13px', lineHeight: 1.4 }}
                                />
                            </Form.Item>

                            {/* Barra Unificada de Parâmetros e Recursos */}
                            <div
                                style={{
                                    background: isDark ? '#1b1b1b' : '#f8fafc',
                                    border: isDark ? '1px solid #303030' : '1px solid #e2e8f0',
                                    borderRadius: 8,
                                    padding: '12px 16px',
                                    marginBottom: 14
                                }}
                            >
                                <Row gutter={[16, 12]} align="middle">
                                    <Col xs={24} sm={8}>
                                        <Form.Item
                                            name="is_principal"
                                            label={<span style={{ fontSize: '12.5px', fontWeight: 500, color: isDark ? '#ffffffd9' : undefined }}>Plano Principal (Padrão)?</span>}
                                            valuePropName="checked"
                                            tooltip="Quando ativo, este plano virá pré-selecionado por padrão no cadastro de novas propostas comerciais."
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Switch
                                                checkedChildren="Sim"
                                                unCheckedChildren="Não"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={8}>
                                        <Form.Item
                                            name="contratacao_horas"
                                            label={<span style={{ fontSize: '12.5px', fontWeight: 500, color: isDark ? '#ffffffd9' : undefined }}>Permite Horas?</span>}
                                            valuePropName="checked"
                                            tooltip="Quando ativo, permite definir quantidade de horas na proposta utilizando o valor padrão."
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Switch
                                                checkedChildren="Sim"
                                                unCheckedChildren="Não"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={8}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <Form.Item
                                                name="possui_tela"
                                                label={<span style={{ fontSize: '12.5px', fontWeight: 500, color: isDark ? '#ffffffd9' : undefined }}>Dispositivo?</span>}
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Switch 
                                                    checkedChildren="Sim"
                                                    unCheckedChildren="Não"                                
                                                />
                                            </Form.Item>

                                            {possuiTelaForm && (
                                                <Form.Item
                                                    name="tipo_tela"
                                                    label={<span style={{ fontSize: '12.5px', fontWeight: 500, color: isDark ? '#ffffffd9' : undefined }}>Tipo</span>}
                                                    rules={[{ required: true, message: 'Selecione o tipo de dispositivo' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select size="small" style={{ width: '115px' }}>
                                                        <Select.Option value="tela85">Tela 85"</Select.Option>
                                                        <Select.Option value="tela">Tela 75"</Select.Option>
                                                        <Select.Option value="totem">Totem 32"</Select.Option>
                                                    </Select>
                                                </Form.Item>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                {possuiTelaForm && (
                                    <>
                                        <Divider style={{ margin: '12px 0 10px 0', borderColor: isDark ? '#303030' : undefined }} />
                                        <Form.Item
                                            name="texto_dispositivo_padrao"
                                            label={<span style={{ fontWeight: 600, fontSize: '12.5px', color: isDark ? '#ffffffd9' : undefined }}>Texto de Apresentação do Dispositivo (Slide 6)</span>}
                                            tooltip="Texto exibido em destaque no slide de apresentação do dispositivo interativo (Tela de 85 polegadas, Tela de 75 polegadas ou Totem de 32 polegadas)."
                                            style={{ marginBottom: 0 }}
                                        >
                                            <TextArea 
                                                rows={3} 
                                                placeholder="Ex: TELA INTERATIVA DE 85 POLEGADAS EM COMODATO.&#10;&#10;ACESSO INCLUSO AOS APLICATIVOS DASHBOARD E GOVFÁCIL." 
                                                style={{ fontSize: '13px', lineHeight: 1.4 }}
                                            />
                                        </Form.Item>
                                    </>
                                )}
                            </div>

                            {/* Detalhamento das Condições (Slide 5) */}
                            <Form.Item
                                name="detalhamento_padrao"
                                label={<span style={{ fontWeight: 600, fontSize: '13px', color: isDark ? '#ffffffd9' : undefined }}>Detalhamento das Condições (Slide 5)</span>}
                                tooltip="Texto das cláusulas e condições exibidas no Slide 5 (Valores e Condições). Use a barra de ferramentas para formatações (*negrito*, _itálico_, marcador •) e inserir a variável {{forma_pagamento}}."
                                style={{ marginBottom: 0 }}
                            >
                                <TextAreaDetalhamento 
                                    rows={5} 
                                    maxLength={650}
                                    placeholder="Descreva as condições, prazos e detalhamento da proposta..." 
                                />
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            )}
            {activeTab === 'geral' && (
                <Card
                    style={{
                        borderRadius: 8,
                        boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.05)',
                        background: isDark ? '#1f1f1f' : undefined,
                        borderColor: isDark ? '#303030' : undefined
                    }}
                    title={
                        <Space>
                            <SettingOutlined style={{ color: '#1890ff' }} />
                            <span style={{ fontSize: '14px', fontWeight: 600, color: isDark ? '#ffffffd9' : undefined }}>Parâmetros Gerais da Proposta Comercial</span>
                        </Space>
                    }
                >
                <Form
                    form={formGeral}
                    layout="vertical"
                    onFinish={handleSalvarConfigGeral}
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={12}>
                            <div style={{ marginBottom: 20 }}>
                                <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 2, color: isDark ? '#ffffffd9' : undefined }}>
                                    Valor Fixo por Hora (R$/h)
                                </Text>
                                <Paragraph type="secondary" style={{ fontSize: '12px', marginBottom: 8, color: isDark ? '#94a3b8' : undefined }}>
                                    Valor unitário padrão cobrado por hora de consultoria/suporte técnico nas propostas comerciais.
                                </Paragraph>

                                <Form.Item
                                    name="valor_hora_padrao"
                                    label={<span style={{ fontSize: '12px', color: isDark ? '#ffffffd9' : undefined }}>Valor Padrão da Hora (R$)</span>}
                                    rules={[{ required: true, message: 'Informe o valor fixo por hora' }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <InputNumber
                                        min={0 as any}
                                        step={1}
                                        precision={2}
                                        decimalSeparator=","
                                        style={{ width: '100%', maxWidth: 260, fontSize: '13px' }}
                                        formatter={(val: any) => {
                                            if (val === undefined || val === null || val === '') return '';
                                            const [int, dec] = String(val).split('.');
                                            const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                                            return dec !== undefined ? `R$ ${formattedInt},${dec}` : `R$ ${formattedInt}`;
                                        }}
                                        parser={(val: any): any => {
                                            if (!val) return '';
                                            let clean = String(val).replace(/[^\d.,]/g, '');
                                            if (clean.includes(',')) {
                                                clean = clean.replace(/\./g, '').replace(',', '.');
                                            } else if (clean.split('.').length > 2 || (clean.includes('.') && /\.\d{3}$/.test(clean))) {
                                                clean = clean.replace(/\./g, '');
                                            }
                                            return clean;
                                        }}
                                    />
                                </Form.Item>
                            </div>

                            <Divider style={{ margin: '16px 0', borderColor: isDark ? '#303030' : undefined }} />

                            <div style={{ marginBottom: 20 }}>
                                <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 2, color: isDark ? '#ffffffd9' : undefined }}>
                                    <UserOutlined style={{ marginRight: 6 }} /> Contato Padrão da Empresa (Diretoria / Slide Final)
                                </Text>
                                <Paragraph type="secondary" style={{ fontSize: '12px', marginBottom: 8, color: isDark ? '#94a3b8' : undefined }}>
                                    Selecione o usuário que figurará como contato padrão da diretoria no slide final de apresentação das propostas comerciais.
                                </Paragraph>

                                <Form.Item
                                    name="contato_padrao_id"
                                    label={<span style={{ fontSize: '12px', color: isDark ? '#ffffffd9' : undefined }}>Contato Principal / Diretor Padrão</span>}
                                    tooltip="Este usuário será carregado por padrão no cadastro de propostas comerciais como contato principal da empresa."
                                    style={{ marginBottom: 0 }}
                                >
                                    <Select
                                        allowClear
                                        showSearch
                                        loading={loadingUsuarios}
                                        placeholder="Selecione o contato padrão da diretoria..."
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            normalizarTexto(option?.children).includes(normalizarTexto(input))
                                        }
                                        style={{ width: '100%', fontSize: '13px' }}
                                    >
                                        {usuarios.map(u => {
                                            const cargoDesc = typeof u.cargo === 'object' ? u.cargo?.nome : u.cargo;
                                            return (
                                                <Option key={u.id} value={u.id}>
                                                    {u.nome}{cargoDesc ? ` (${cargoDesc})` : ''}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            </div>
                        </Col>

                        <Col xs={24} lg={12}>
                            <div style={{ marginBottom: 20 }}>
                                <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 2, color: isDark ? '#ffffffd9' : undefined }}>
                                    Slide 2 - Municípios Atendidos
                                </Text>
                                <Paragraph type="secondary" style={{ fontSize: '12px', marginBottom: 8, color: isDark ? '#94a3b8' : undefined }}>
                                    Selecione e ordene as cidades ou associações com contrato ativo no sistema que serão exibidas na lista de municípios atendidos do Slide 2 (mínimo de 20 e máximo de 25).
                                </Paragraph>

                                <Form.Item
                                    name="municipios_destaque"
                                    rules={[
                                        { required: true, message: 'Selecione os municípios ou associações em destaque' },
                                        {
                                            validator: (_, val) => {
                                                const total = Array.isArray(val) ? val.length : 0;
                                                if (total < 20) {
                                                    return Promise.reject(new Error(`Selecione no mínimo 20 municípios ou associações (atualmente com ${total})`));
                                                }
                                                if (total > 25) {
                                                    return Promise.reject(new Error(`Selecione no máximo 25 municípios ou associações (atualmente com ${total})`));
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <MunicipiosOrdenavelList
                                        cidadesDisponiveis={cidadesAtivas}
                                        loading={loadingCidadesAtivas}
                                        isDark={isDark}
                                        minLimit={20}
                                        maxLimit={25}
                                    />
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '16px 0', borderColor: isDark ? '#303030' : undefined }} />

                    <Form.Item style={{ margin: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={salvandoGeral}
                            style={{ minWidth: 180, height: 36, fontSize: '13px' }}
                        >
                            Salvar Configurações Gerais
                        </Button>
                    </Form.Item>
                </Form>
                </Card>
            )}
        </div>
    );
};

export default ConfiguracaoPlanos;
